import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserCheck } from 'lucide-react';
import {
  FriendAccessLevel,
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVEL_DESCRIPTIONS,
} from '../types/friends';
import { motion, AnimatePresence } from 'framer-motion';

interface AcceptFriendRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requesterName: string;
  requesterAccessLevel: FriendAccessLevel; // Access level requester is giving to you
  onAccept: (yourAccessLevel: FriendAccessLevel) => Promise<void>;
}

const AcceptFriendRequestModal = ({
  isOpen,
  onClose,
  requesterName,
  onAccept,
}: AcceptFriendRequestModalProps) => {
  const [yourAccessLevel, setYourAccessLevel] = useState<FriendAccessLevel>('restricted-access');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    setError('');
    setIsLoading(true);
    try {
      await onAccept(yourAccessLevel);
      setYourAccessLevel('restricted-access'); // Reset for next time
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to accept friend request');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-teal-600" />
                  Accept Friend Request
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-semibold">{requesterName}</span> wants to be your friend.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Choose Access Level to Give {requesterName}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Select what level of access you want to give {requesterName} to your cruise calendar.
                    <span className="block mt-1 text-gray-400 italic">
                      This is your initial access level and can be modified later from the Friends page.
                    </span>
                  </p>
                  <div className="space-y-3">
                    {(['full-access', 'restricted-access'] as FriendAccessLevel[]).map((level) => (
                      <label
                        key={level}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          yourAccessLevel === level
                            ? 'border-secondary-500 bg-secondary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="yourAccessLevel"
                          value={level}
                          checked={yourAccessLevel === level}
                          onChange={(e) =>
                            setYourAccessLevel(e.target.value as FriendAccessLevel)
                          }
                          className="mt-1 mr-3"
                          disabled={isLoading}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">
                              {ACCESS_LEVEL_LABELS[level]}
                            </span>
                            {level === 'full-access' && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {ACCESS_LEVEL_DESCRIPTIONS[level]}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    className="flex-1 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Accepting...' : 'Accept Request'}
                  </button>
                </div>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default AcceptFriendRequestModal;

